import React, { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import Svg, { Circle, Line, Polyline } from 'react-native-svg';
import ChartSkeleton from '../../../components/ChartSkeleton';
import TableSkeleton from '../../../components/TableSkeleton';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import sharedStyles from '../OrderRulesReport.styles';
import executiveStyles from './ExecutiveOverviewTab.styles';
import {
  OrderRulesEnrollmentTrendPoint,
  OrderRulesKpiMetric,
  OrderRulesLayerPerformanceTrendPoint,
  OrderRulesMaxPotentialExecutionRow,
  OrderRulesSkuRecommendationSummaryRow,
} from '../orderRulesApi';

interface ExecutiveOverviewTabProps {
  loading: boolean;
  summaryLoading?: boolean;
  maxPotentialLoading?: boolean;
  enrollmentTrendLoading?: boolean;
  layerPerformanceLoading?: boolean;
  kpis: OrderRulesKpiMetric[];
  summaryDrillLevel?: string;
  maxPotentialDrillLevel?: string;
  skuRecommendationSummary: OrderRulesSkuRecommendationSummaryRow[];
  maxPotentialExecution: OrderRulesMaxPotentialExecutionRow[];
  enrollmentTrend: OrderRulesEnrollmentTrendPoint[];
  enrollmentTimeGrain?: string;
  enrollmentXAxisLabel?: string;
  enrollmentYAxisLabel?: string;
  layerPerformanceTrend: OrderRulesLayerPerformanceTrendPoint[];
  enrollmentInsufficientDataMessage?: string;
  layerPerformanceInsufficientDataMessage?: string;
  layerPerformanceTimeGrain?: string;
  layerPerformanceXAxisLabel?: string;
  layerPerformanceYAxisLabel?: string;
  maxPotentialViewMode?: 'percent' | 'cases';
  onMaxPotentialViewModeChange?: (mode: 'percent' | 'cases') => void;
}

const styles = { ...sharedStyles, ...executiveStyles };

// Minimal per-column-group tints distinguishing High / Medium / Low / Total in the summary table.
const SUMMARY_GROUP_CELL_STYLES = [
  styles.summaryGroupCellHigh,
  styles.summaryGroupCellMedium,
  styles.summaryGroupCellLow,
  styles.summaryGroupCellTotal,
];

type SortDir = 'asc' | 'desc' | null;

interface SortState {
  col: string | null;
  dir: SortDir;
}

const TOOLTIP_GAP = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 44;

const SUMMARY_COLUMNS: Array<{ key: keyof OrderRulesSkuRecommendationSummaryRow; label: string; groupIndex: number }> = [
  { key: 'highTurnOn', label: 'Turn On', groupIndex: 0 },
  { key: 'highTurnOff', label: 'Turn Off', groupIndex: 0 },
  { key: 'mediumTurnOn', label: 'Turn On', groupIndex: 1 },
  { key: 'mediumTurnOff', label: 'Turn Off', groupIndex: 1 },
  { key: 'lowTurnOn', label: 'Turn On', groupIndex: 2 },
  { key: 'lowTurnOff', label: 'Turn Off', groupIndex: 2 },
  { key: 'totalTurnOn', label: 'Turn On', groupIndex: 3 },
  { key: 'totalTurnOff', label: 'Turn Off', groupIndex: 3 },
];

const formatInt = (value: number): string => value.toLocaleString('en-US');

const TIME_GRAIN_LABELS: Record<string, string> = {
  YEAR: 'Year',
  PERIOD: 'Period',
  WEEK: 'Week',
};

const formatTimeGrainLabel = (timeGrain?: string): string =>
  TIME_GRAIN_LABELS[(timeGrain || '').toUpperCase()] || 'Year / Period / Week';

const buildLinePoints = (values: number[], width: number, height: number, min: number, max: number): string => {
  if (values.length === 0) return '';
  return values
    .map((value, index) => {
      const x = values.length === 1 ? width / 2 : (index / (values.length - 1)) * width;
      const normalized = (value - min) / (max - min || 1);
      const y = height - normalized * height;
      return `${x},${y}`;
    })
    .join(' ');
};

/**
 * Compute Y-axis bounds with a 5pp buffer above and below the actual data.
 * Works for any mix of data series (e.g. enrolled + target for enrollment trend).
 * Returns { yMin, yMax, yTicks } where yTicks is exactly 5 evenly-spaced labels.
 */
const computeYAxisBounds = (
  ...seriesArrays: number[][]
): { yMin: number; yMax: number; yTicks: number[] } => {
  const all = ([] as number[]).concat(...seriesArrays).filter((v) => Number.isFinite(v));
  if (all.length === 0) return { yMin: 0, yMax: 100, yTicks: [0, 25, 50, 75, 100] };

  const dataMin = Math.min(...all);
  const dataMax = Math.max(...all);
  const buffer = (dataMax - dataMin) * 0.05 || 5; // 5% of range, min 5pp when range is 0
  const rawMin = dataMin - buffer;
  const rawMax = dataMax + buffer;

  // Clamp to [0, 100] since these are percentage values
  const yMin = Math.max(0, Math.floor(rawMin));
  const yMax = Math.min(100, Math.ceil(rawMax));

  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) / 4) * i);
  return { yMin, yMax, yTicks };
};

const ExecutiveOverviewTab: React.FC<ExecutiveOverviewTabProps> = ({
  loading,
  summaryLoading = false,
  maxPotentialLoading = false,
  enrollmentTrendLoading = false,
  layerPerformanceLoading = false,
  kpis,
  summaryDrillLevel,
  maxPotentialDrillLevel,
  skuRecommendationSummary,
  maxPotentialExecution,
  enrollmentTrend,
  enrollmentTimeGrain,
  enrollmentXAxisLabel,
  enrollmentYAxisLabel,
  layerPerformanceTrend,
  enrollmentInsufficientDataMessage,
  layerPerformanceInsufficientDataMessage,
  layerPerformanceTimeGrain,
  layerPerformanceXAxisLabel,
  layerPerformanceYAxisLabel,
  maxPotentialViewMode: controlledMaxPotentialViewMode,
  onMaxPotentialViewModeChange,
}) => {
  const { width } = useWindowDimensions();
  const [hoveredSummaryRowIndex, setHoveredSummaryRowIndex] = useState<number | null>(null);
  const [hoveredKpiId, setHoveredKpiId] = useState<string | null>(null);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const [internalMaxPotentialViewMode, setInternalMaxPotentialViewMode] = useState<'percent' | 'cases'>('percent');
  const maxPotentialViewMode = controlledMaxPotentialViewMode ?? internalMaxPotentialViewMode;
  const setMaxPotentialViewMode = useCallback(
    (mode: 'percent' | 'cases') => {
      setInternalMaxPotentialViewMode(mode);
      onMaxPotentialViewModeChange?.(mode);
    },
    [onMaxPotentialViewModeChange],
  );
  const [enrollmentCardWidth, setEnrollmentCardWidth] = useState<number>(0);
  const [layerCardWidth, setLayerCardWidth] = useState<number>(0);
  const [selectedEnrollmentPointIndex, setSelectedEnrollmentPointIndex] = useState<number | null>(null);
  const [selectedLayerPointIndex, setSelectedLayerPointIndex] = useState<number | null>(null);
  const [summarySort, setSummarySort] = useState<SortState>({ col: null, dir: null });

  const handleSummarySort = (colKey: string) => {
    setSummarySort((current) => {
      if (current.col !== colKey) return { col: colKey, dir: 'asc' };
      if (current.dir === 'asc') return { col: colKey, dir: 'desc' };
      if (current.dir === 'desc') return { col: null, dir: null };
      return { col: colKey, dir: 'asc' };
    });
  };

  const kpiTooltips: Record<string, string> = {
    'sku-on': 'Count of SKU and customer combinations identified by the recommendation logic as candidates to have an order rule enabled, based on historical ordering behavior and qualification thresholds.',
    'sku-off': 'Count of SKU and customer combinations identified as candidates to have an order rule disabled or not deployed, typically where ordering behavior no longer supports rule use.',
    'avg-enrollment': 'Percentage of eligible customers actually enrolled in order rules. Execution adoption metric showing implementation versus opportunity.',
    savings: 'Estimated cost savings if open layer recommendations are adopted, based on layer opportunity volume and the handling cost difference between layer and bulk picking.',
    'check-flags': 'Count of records where order rule setup or supporting data fails validation checks, signaling mismatches across systems or configuration issues needing correction.',
  };

  const renderSkuRecommendationSummary = (rows: OrderRulesSkuRecommendationSummaryRow[]) => {
    if (summaryLoading) return <TableSkeleton rows={10} />;

    const NUMERIC_KEYS: Array<keyof OrderRulesSkuRecommendationSummaryRow> = [
      'highTurnOn', 'highTurnOff', 'mediumTurnOn', 'mediumTurnOff',
      'lowTurnOn', 'lowTurnOff', 'totalTurnOn', 'totalTurnOff',
    ];
    const hasData =
      rows &&
      rows.length > 0 &&
      rows.some((row) => NUMERIC_KEYS.some((k) => Number(row[k]) !== 0));

    if (!hasData) {
      return (
        <View style={styles.emptyStateWrap}>
          <Text style={styles.emptyStateText}>No recommendation data for the current selection.</Text>
        </View>
      );
    }

    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator
        style={styles.summaryHorizontalScroll}
        contentContainerStyle={styles.summaryScrollContent}
      >
        <View style={styles.summaryTableWrap}>
          {/* Header lives inside the vertical ScrollView as a sticky first child so header and
              body share the same width — a separate header would stay full width while the body
              shrinks by the scrollbar's width, offsetting the High/Medium/Low dividers. */}
          <ScrollView
            style={styles.summaryBodyScroll}
            showsVerticalScrollIndicator
            nestedScrollEnabled
            stickyHeaderIndices={[0]}
          >
            <View style={styles.summaryHeaderWrap}>
              <View style={styles.summaryHeaderTopRow}>
                <View style={[styles.summaryHeaderPrimaryCell, styles.summaryRegionCell]} />
                {['High', 'Medium', 'Low', 'Total'].map((label, groupIndex) => (
                  <View key={label} style={[styles.summaryHeaderGroupCell, SUMMARY_GROUP_CELL_STYLES[groupIndex]]}>
                    <Text style={styles.summaryHeaderText}>{label}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.summaryHeaderBottomRow}>
                <TouchableOpacity
                  style={[styles.summaryHeaderSecondaryCell, styles.summaryRegionCell]}
                  onPress={() => handleSummarySort('region')}
                  activeOpacity={0.7}
                  accessibilityLabel="Sort by geography"
                >
                  <Text style={styles.summaryHeaderText}>
                    {(() => {
                      const level = (summaryDrillLevel || '').toUpperCase();
                      if (level === 'LOCATION') return 'LOCATION';
                      if (level === 'MARKET') return 'MARKET / POD';
                      return 'REGION / UNIT';
                    })()}
                  </Text>
                  <MaterialIcons
                    name={summarySort.col === 'region' ? (summarySort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                    size={12}
                    color={summarySort.col === 'region' ? '#005CBC' : '#9E9E9E'}
                    style={styles.tableHeaderSortIcon}
                  />
                </TouchableOpacity>
                {SUMMARY_COLUMNS.map((col, index) => {
                  const isGroupEnd = index % 2 === 1;
                  const isActive = summarySort.col === col.key;
                  return (
                    <TouchableOpacity
                      key={col.key}
                      style={[
                        styles.summaryHeaderSecondaryCell,
                        SUMMARY_GROUP_CELL_STYLES[col.groupIndex],
                        isGroupEnd && styles.summaryGroupDivider,
                      ]}
                      onPress={() => handleSummarySort(col.key)}
                      activeOpacity={0.7}
                      accessibilityLabel={`Sort by ${col.label}`}
                    >
                      <Text style={styles.summaryHeaderText}>{col.label}</Text>
                      <MaterialIcons
                        name={isActive ? (summarySort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                        size={12}
                        color={isActive ? '#005CBC' : '#9E9E9E'}
                        style={styles.tableHeaderSortIcon}
                      />
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {(() => {
              const grandTotalRows = rows.filter((r) => r.isGrandTotal);
              const nonGrandTotalRows = rows.filter((r) => !r.isGrandTotal);

              if (summarySort.col && summarySort.dir) {
                const colKey = summarySort.col as keyof OrderRulesSkuRecommendationSummaryRow;
                const dir = summarySort.dir;
                nonGrandTotalRows.sort((a, b) => {
                  if (colKey === 'region') {
                    const cmp = String(a.region ?? '').localeCompare(String(b.region ?? ''), undefined, { numeric: true, sensitivity: 'base' });
                    return dir === 'asc' ? cmp : -cmp;
                  }
                  const aVal = Number(a[colKey] ?? 0);
                  const bVal = Number(b[colKey] ?? 0);
                  return dir === 'asc' ? aVal - bVal : bVal - aVal;
                });
              }

              return [...grandTotalRows, ...nonGrandTotalRows];
            })()
              .map((row, index) => {
            const isRowHoverable = !row.isGrandTotal;
            const isRowHovered = hoveredSummaryRowIndex === index;
            const rowStyle = row.isGrandTotal
              ? [styles.summaryDataRow, styles.summaryGrandTotalRow]
              : [styles.summaryDataRow, index % 2 === 0 ? styles.summaryAltRow : null, isRowHovered ? styles.summaryHoverRow : null];

            return (
              <Pressable
                key={`${row.region}-${index}`}
                style={rowStyle}
                onHoverIn={isRowHoverable ? () => setHoveredSummaryRowIndex(index) : undefined}
                onHoverOut={isRowHoverable ? () => setHoveredSummaryRowIndex(null) : undefined}
              >
                <View style={[styles.summaryDataCell, styles.summaryRegionCell]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{row.region}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[0]]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.highTurnOn)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[0], styles.summaryGroupDivider]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.highTurnOff)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[1]]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.mediumTurnOn)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[1], styles.summaryGroupDivider]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.mediumTurnOff)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[2]]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.lowTurnOn)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[2], styles.summaryGroupDivider]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.lowTurnOff)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[3]]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.totalTurnOn)}</Text></View>
                <View style={[styles.summaryDataCell, SUMMARY_GROUP_CELL_STYLES[3], styles.summaryGroupDivider]}><Text style={[styles.summaryDataText, row.isGrandTotal && styles.summaryDataTextStrong]}>{formatInt(row.totalTurnOff)}</Text></View>
              </Pressable>
            );
          })}
          </ScrollView>
        </View>
      </ScrollView>
    );
  };

  const renderMaxPotentialExecution = (rows: OrderRulesMaxPotentialExecutionRow[]) => {
    if (maxPotentialLoading) return <ChartSkeleton height={300} barMode />;
    if (!rows || rows.length === 0) {
      return <View style={styles.emptyStateWrap}><Text style={styles.emptyStateText}>No max potential execution data available.</Text></View>;
    }

    // Sort rows descending by max potential value in current view mode
    const sortedRows = [...rows].sort((a, b) => {
      const maxA = maxPotentialViewMode === 'percent' ? a.maxPotentialPct : a.maxPotentialCases;
      const maxB = maxPotentialViewMode === 'percent' ? b.maxPotentialPct : b.maxPotentialCases;
      return (Number(maxB) || 0) - (Number(maxA) || 0);
    });

    // Find highest max value across all rows to scale the bars against the full track (100%)
    const globalMax = Math.max(
      ...sortedRows.map((r) => (maxPotentialViewMode === 'percent' ? r.maxPotentialPct : r.maxPotentialCases)),
      1
    );

    return (
      <View style={styles.maxPotentialChartBody}>
        <View style={styles.maxPotentialBarsContent}>
          <View style={styles.barHeaderRow}>
            <View style={styles.barHeaderNameSpacer} />
            <Text style={styles.barHeaderMainText}>ACTUAL TO MAX POTENTIAL</Text>
            <Text style={[styles.barHeaderGapText, maxPotentialViewMode === 'cases' && styles.barHeaderGapTextCases]}>MAX</Text>
          </View>

          <View style={styles.barListWrap}>
            {sortedRows.map((row, index) => {
              const actualValue = maxPotentialViewMode === 'percent' ? row.layerPerformancePct : row.actualCases;
              const gapValue = maxPotentialViewMode === 'percent' ? row.gapPct : row.gapCases;
              const maxPotentialValue = maxPotentialViewMode === 'percent' ? row.maxPotentialPct : row.maxPotentialCases;

              // Scale actual and gap as a percentage of globalMax so the colored portions scale proportionally
              const actualWidth = globalMax > 0 ? Math.min(100, (actualValue / globalMax) * 100) : 0;
              const gapWidth = globalMax > 0 ? Math.min(100 - actualWidth, (gapValue / globalMax) * 100) : 0;

              const actualLabel = maxPotentialViewMode === 'percent' ? `${Number(row.layerPerformancePct).toFixed(0)}%` : formatInt(row.actualCases);
              const gapLabel = maxPotentialViewMode === 'percent' ? `${Number(row.gapPct).toFixed(0)}%` : formatInt(row.gapCases);
              const maxPotentialLabel = maxPotentialViewMode === 'percent' ? `${Number(maxPotentialValue).toFixed(0)}%` : formatInt(maxPotentialValue);

              // Determine if there is enough horizontal space to show text inside the bar segments
              const hasSpaceForActual =
                maxPotentialViewMode === 'percent'
                  ? actualWidth >= 8
                  : actualWidth >= Math.max(10, actualLabel.length * 2.2);
              const hasSpaceForGap =
                maxPotentialViewMode === 'percent'
                  ? gapWidth >= 8
                  : gapWidth >= Math.max(10, gapLabel.length * 2.2);

              const isHovered = hoveredBarIndex === index;

              return (
                <Pressable
                  key={`${row.geographyName || row.region}-${index}`}
                  style={[styles.barRow, { position: 'relative', zIndex: isHovered ? 10 : 1 }]}
                  onHoverIn={Platform.OS === 'web' ? () => setHoveredBarIndex(index) : undefined}
                  onHoverOut={Platform.OS === 'web' ? () => setHoveredBarIndex(null) : undefined}
                  onPress={() => setHoveredBarIndex((prev) => (prev === index ? null : index))}
                >
                  <Text style={styles.barRowLabel} numberOfLines={1}>{row.geographyName || row.region}</Text>
                  <View style={styles.barTrack}>
                    <View style={[styles.barActual, { width: `${actualWidth}%` }]}>
                      {hasSpaceForActual ? (
                        <Text style={styles.barActualText} numberOfLines={1}>{actualLabel}</Text>
                      ) : null}
                    </View>
                    {gapWidth > 0 ? (
                      <View style={[styles.barGap, { width: `${gapWidth}%` }]}>
                        {hasSpaceForGap ? (
                          <Text style={styles.barGapText} numberOfLines={1}>{gapLabel}</Text>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                  <Text style={[styles.barGapValue, maxPotentialViewMode === 'cases' && styles.barGapValueCases]}>{maxPotentialLabel}</Text>

                  {isHovered ? (
                    <View style={styles.barTooltip} pointerEvents="none">
                      <Text style={styles.barTooltipTitle} numberOfLines={1}>
                        {row.geographyName || row.region}
                      </Text>
                      <View style={styles.barTooltipRow}>
                        <View style={styles.barTooltipLabelWrap}>
                          <View style={[styles.barTooltipDot, { backgroundColor: '#005CBC' }]} />
                          <Text style={styles.barTooltipLabel}>Layer Performance:</Text>
                        </View>
                        <Text style={styles.barTooltipValue}>
                          {actualLabel}
                          {maxPotentialViewMode === 'cases' && row.layerPerformancePct != null
                            ? ` (${Number(row.layerPerformancePct).toFixed(0)}%)`
                            : ''}
                        </Text>
                      </View>
                      {Number(row.gapPct) > 0 || Number(row.gapCases) > 0 ? (
                        <View style={styles.barTooltipRow}>
                          <View style={styles.barTooltipLabelWrap}>
                            <View style={[styles.barTooltipDot, { backgroundColor: '#FF9800' }]} />
                            <Text style={styles.barTooltipLabel}>Gap:</Text>
                          </View>
                          <Text style={styles.barTooltipValue}>
                            {gapLabel}
                            {maxPotentialViewMode === 'cases' && row.gapPct != null
                              ? ` (${Number(row.gapPct).toFixed(0)}%)`
                              : ''}
                          </Text>
                        </View>
                      ) : null}
                      <View style={styles.barTooltipRow}>
                        <View style={styles.barTooltipLabelWrap}>
                          <View style={[styles.barTooltipDot, { backgroundColor: '#9CA3AF' }]} />
                          <Text style={styles.barTooltipLabel}>Max Potential:</Text>
                        </View>
                        <Text style={styles.barTooltipValue}>
                          {maxPotentialLabel}
                          {maxPotentialViewMode === 'cases' && row.maxPotentialPct != null
                            ? ` (${Number(row.maxPotentialPct).toFixed(0)}%)`
                            : ''}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </View>

        <View style={styles.barLegendWrap}>
          <View style={styles.barLegendItem}><View style={[styles.barLegendDot, { backgroundColor: '#005CBC' }]} /><Text style={styles.barLegendText}>Layer Performance</Text></View>
          <View style={styles.barLegendItem}><View style={[styles.barLegendDot, { backgroundColor: '#FF9800' }]} /><Text style={styles.barLegendText}>Gap</Text></View>
        </View>
      </View>
    );
  };

  const renderEnrollmentTrend = (rows: OrderRulesEnrollmentTrendPoint[]) => {
    if (enrollmentTrendLoading) return <ChartSkeleton height={220} />;
    if (enrollmentInsufficientDataMessage) {
      return (
        <View style={styles.emptyStateWrap}>
          <Text style={styles.emptyStateText}>{enrollmentInsufficientDataMessage}</Text>
        </View>
      );
    }
    if (!rows || rows.length === 0) {
      return <View style={styles.emptyStateWrap}><Text style={styles.emptyStateText}>No enrollment trend data available.</Text></View>;
    }

    const PADDING_LEFT = 40;
    const PADDING_RIGHT = 36;
    const availableWidth = Math.max(300, (enrollmentCardWidth || 560) - 32);
    const plotWidth = Math.max(220, availableWidth - PADDING_LEFT - PADDING_RIGHT);
    const svgWidth = plotWidth + PADDING_LEFT + PADDING_RIGHT;
    const chartHeight = 150;
    const enrolledValues = rows.map((row) => row.enrolledPct);
    const targetValues = rows.map((row) => row.targetPct);
    const { yMin, yMax, yTicks } = computeYAxisBounds(enrolledValues, targetValues);
    const xStep = rows.length > 1 ? plotWidth / (rows.length - 1) : 0;
    const targetPct = rows[0]?.targetPct;

    return (
      <View
        style={styles.trendChartContainer}
        onLayout={(e: LayoutChangeEvent) => setEnrollmentCardWidth(e.nativeEvent.layout.width)}
      >
        <View style={[styles.trendChartWrap, { position: 'relative' }]}>
          <Svg width={svgWidth} height={chartHeight + 56} viewBox={`0 0 ${svgWidth} ${chartHeight + 56}`} style={{ width: '100%' }}>
            {yTicks.map((tick) => {
              const y = ((yMax - tick) / (yMax - yMin || 1)) * chartHeight + 12;
              return <Line key={`enrollment-y-${tick}`} x1={PADDING_LEFT} y1={y} x2={PADDING_LEFT + plotWidth} y2={y} stroke="#E8EDF6" strokeWidth={1} />;
            })}

            <Polyline
              points={buildLinePoints(targetValues, plotWidth, chartHeight, yMin, yMax)
                .split(' ')
                .map((point) => {
                  const [x, y] = point.split(',');
                  return `${Number(x) + PADDING_LEFT},${Number(y) + 12}`;
                })
                .join(' ')}
              fill="none"
              stroke="#E2549A"
              strokeWidth={2}
              strokeDasharray="4 4"
            />

            <Polyline
              points={buildLinePoints(enrolledValues, plotWidth, chartHeight, yMin, yMax)
                .split(' ')
                .map((point) => {
                  const [x, y] = point.split(',');
                  return `${Number(x) + PADDING_LEFT},${Number(y) + 12}`;
                })
                .join(' ')}
              fill="none"
              stroke="#005CBC"
              strokeWidth={2.5}
            />

            {rows.map((row, index) => {
              const x = PADDING_LEFT + xStep * index;
              const y = ((yMax - row.enrolledPct) / (yMax - yMin || 1)) * chartHeight + 12;
              return <Circle key={`enrollment-dot-${index}`} cx={x} cy={y} r={3.2} fill="#005CBC" stroke="#FFFFFF" strokeWidth={1.2} />;
            })}
          </Svg>

          {/* Interactive hit areas & tooltips for data points */}
          {rows.map((row, index) => {
            const pointX = PADDING_LEFT + xStep * index;
            const pointY = ((yMax - row.enrolledPct) / (yMax - yMin || 1)) * chartHeight + 12;
            const isSelected = selectedEnrollmentPointIndex === index;
            const showAbove = pointY >= TOOLTIP_ESTIMATED_HEIGHT + TOOLTIP_GAP + 14;
            const deltaText = typeof row.pointOverPointChange === 'number'
              ? `${row.pointOverPointChange > 0 ? '+' : ''}${row.pointOverPointChange.toFixed(2)}pp`
              : null;
            const tooltipValue = `${row.enrolledPct}%`;
            const maxTextLength = Math.max(tooltipValue.length, deltaText ? deltaText.length : 0);
            const tooltipWidth = Math.max(54, maxTextLength * 7.5 + 16);
            const hitLeft = pointX - 14;
            const tooltipLeft = Math.max(4 - hitLeft, Math.min(14 - tooltipWidth / 2, svgWidth - tooltipWidth - 4 - hitLeft));

            return (
              <Pressable
                key={`enrollment-hit-${row.year || row.timePeriod || index}`}
                onPress={() => setSelectedEnrollmentPointIndex((prev) => (prev === index ? null : index))}
                onHoverIn={() => setSelectedEnrollmentPointIndex(index)}
                onHoverOut={() => setSelectedEnrollmentPointIndex((prev) => (prev === index ? null : prev))}
                style={[styles.trendPointHitArea, { left: pointX - 14, top: Math.max(0, pointY - 14) }]}
              >
                {isSelected ? (
                  <View
                    style={[
                      styles.trendTooltip,
                      {
                        width: tooltipWidth,
                        left: tooltipLeft,
                        ...(showAbove ? { bottom: 22 + TOOLTIP_GAP } : { top: 22 + TOOLTIP_GAP }),
                      },
                    ]}
                  >
                    <Text style={styles.trendTooltipValue}>{tooltipValue}</Text>
                    {deltaText ? (
                      <Text
                        style={[
                          styles.trendTooltipDelta,
                          (row.pointOverPointChange ?? 0) >= 0
                            ? styles.trendTooltipDeltaPositive
                            : styles.trendTooltipDeltaNegative,
                        ]}
                      >
                        {deltaText}
                      </Text>
                    ) : null}
                  </View>
                ) : null}
              </Pressable>
            );
          })}

          <View style={[StyleSheet.absoluteFillObject, { width: svgWidth, height: chartHeight + 56 }]} pointerEvents="none">
            {yTicks.map((tick) => {
              const y = ((yMax - tick) / (yMax - yMin || 1)) * chartHeight + 12;
              return (
                <Text key={`enrollment-y-label-${tick}`} style={[styles.trendAxisTickText, { left: 2, top: y - 6 }]}>
                  {Math.round(tick)}%
                </Text>
              );
            })}

            {rows.map((row, index) => {
              const x = PADDING_LEFT + xStep * index;
              const label = row.year || row.timePeriod || 'Period';

              return (
                <Text
                  key={`enrollment-point-label-${label ?? index}`}
                  style={[styles.trendAxisPeriodText, { left: x - 30, top: chartHeight + 28, width: 60 }]}
                >
                  {label}
                </Text>
              );
            })}
          </View>
        </View>

        <View style={styles.chartLegendRow}>
          <View style={styles.chartLegendItem}><View style={[styles.chartLegendDot, { backgroundColor: '#005CBC' }]} /><Text style={styles.chartLegendText}>Enrolled %</Text></View>
          <View style={styles.chartLegendItem}><View style={[styles.chartLegendLineDash, { borderColor: '#E2549A' }]} /><Text style={styles.chartLegendText}>{`Target (${targetPct ?? 75}%)`}</Text></View>
        </View>
      </View>
    );
  };

  const renderLayerPerformance = (rows: OrderRulesLayerPerformanceTrendPoint[]) => {
    if (layerPerformanceLoading) return <ChartSkeleton height={220} />;
    if (layerPerformanceInsufficientDataMessage) {
      return (
        <View style={styles.emptyStateWrap}>
          <Text style={styles.emptyStateText}>{layerPerformanceInsufficientDataMessage}</Text>
        </View>
      );
    }
    if (!rows || rows.length === 0) {
      return (
        <View style={styles.emptyStateWrap}>
          <Text style={styles.emptyStateText}>No layer performance trend data available.</Text>
        </View>
      );
    }

    const PADDING_LEFT = 40;
    const PADDING_RIGHT = 36;
    const availableWidth = Math.max(300, (layerCardWidth || 560) - 32);
    const plotWidth = Math.max(220, availableWidth - PADDING_LEFT - PADDING_RIGHT);
    const svgWidth = plotWidth + PADDING_LEFT + PADDING_RIGHT;
    const chartHeight = 150;
    const values = rows.map((row) => row.valuePct);
    const { yMin, yMax, yTicks } = computeYAxisBounds(values);
    const xStep = rows.length > 1 ? plotWidth / (rows.length - 1) : 0;

    return (
      <View
        style={styles.trendChartContainer}
        onLayout={(e: LayoutChangeEvent) => setLayerCardWidth(e.nativeEvent.layout.width)}
      >
        <View style={[styles.trendChartWrap, { position: 'relative' }]}>
          <Svg width={svgWidth} height={chartHeight + 56} viewBox={`0 0 ${svgWidth} ${chartHeight + 56}`} style={{ width: '100%' }}>
            {yTicks.map((tick) => {
              const y = ((yMax - tick) / (yMax - yMin || 1)) * chartHeight + 12;
              return <Line key={`layer-y-${tick}`} x1={PADDING_LEFT} y1={y} x2={PADDING_LEFT + plotWidth} y2={y} stroke="#E8EDF6" strokeWidth={1} />;
            })}

            <Polyline
              points={buildLinePoints(values, plotWidth, chartHeight, yMin, yMax)
                .split(' ')
                .map((point) => {
                  const [x, y] = point.split(',');
                  return `${Number(x) + PADDING_LEFT},${Number(y) + 12}`;
                })
                .join(' ')}
              fill="none"
              stroke="#00666B"
              strokeWidth={2.5}
            />

            {rows.map((row, index) => {
              const x = PADDING_LEFT + xStep * index;
              const y = ((yMax - row.valuePct) / (yMax - yMin || 1)) * chartHeight + 12;
              return <Circle key={`layer-dot-${index}`} cx={x} cy={y} r={3.2} fill="#00666B" stroke="#FFFFFF" strokeWidth={1.2} />;
            })}
          </Svg>

          {/* Interactive hit areas & tooltips for data points */}
          {rows.map((row, index) => {
            const pointX = PADDING_LEFT + xStep * index;
            const pointY = ((yMax - row.valuePct) / (yMax - yMin || 1)) * chartHeight + 12;
            const isSelected = selectedLayerPointIndex === index;
            const showAbove = pointY >= TOOLTIP_ESTIMATED_HEIGHT + TOOLTIP_GAP + 14;
            const deltaText = typeof row.pointOverPointChange === 'number'
              ? `${row.pointOverPointChange > 0 ? '+' : ''}${row.pointOverPointChange.toFixed(2)}pp`
              : null;
            const tooltipValue = `${row.valuePct}%`;
            const maxTextLength = Math.max(tooltipValue.length, deltaText ? deltaText.length : 0);
            const tooltipWidth = Math.max(54, maxTextLength * 7.5 + 16);
            const hitLeft = pointX - 14;
            const tooltipLeft = Math.max(4 - hitLeft, Math.min(14 - tooltipWidth / 2, svgWidth - tooltipWidth - 4 - hitLeft));

            return (
              <Pressable
                key={`layer-hit-${row.label || row.timePeriod || index}`}
                onPress={() => setSelectedLayerPointIndex((prev) => (prev === index ? null : index))}
                onHoverIn={() => setSelectedLayerPointIndex(index)}
                onHoverOut={() => setSelectedLayerPointIndex((prev) => (prev === index ? null : prev))}
                style={[styles.trendPointHitArea, { left: pointX - 14, top: Math.max(0, pointY - 14) }]}
              >
                {isSelected ? (
                  <View
                    style={[
                      styles.trendTooltip,
                      {
                        width: tooltipWidth,
                        left: tooltipLeft,
                        ...(showAbove ? { bottom: 22 + TOOLTIP_GAP } : { top: 22 + TOOLTIP_GAP }),
                      },
                    ]}
                  >
                    <Text style={styles.trendTooltipValue}>{tooltipValue}</Text>
                    {deltaText ? (
                      <Text
                        style={[
                          styles.trendTooltipDelta,
                          (row.pointOverPointChange ?? 0) >= 0
                            ? styles.trendTooltipDeltaPositive
                            : styles.trendTooltipDeltaNegative,
                        ]}
                      >
                        {deltaText}
                      </Text>
                    ) : null}
                  </View>
                ) : null}
              </Pressable>
            );
          })}

          <View style={[StyleSheet.absoluteFillObject, { width: svgWidth, height: chartHeight + 56 }]} pointerEvents="none">
            {yTicks.map((tick) => {
              const y = ((yMax - tick) / (yMax - yMin || 1)) * chartHeight + 12;
              return (
                <Text key={`layer-y-label-${tick}`} style={[styles.trendAxisTickText, { left: 2, top: y - 6 }]}>
                  {Math.round(tick)}%
                </Text>
              );
            })}

            {rows.map((row, index) => {
              const x = PADDING_LEFT + xStep * index;
              const label = row.label || row.timePeriod || 'Period';

              return (
                <Text
                  key={`layer-point-label-${label ?? index}`}
                  style={[styles.trendAxisPeriodText, { left: x - 30, top: chartHeight + 28, width: 60 }]}
                >
                  {label}
                </Text>
              );
            })}
          </View>
        </View>

        <View style={styles.chartLegendRow}>
          <View style={styles.chartLegendItem}><View style={[styles.chartLegendDot, { backgroundColor: '#00666B' }]} /><Text style={styles.chartLegendText}>Layer Performance %</Text></View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.sectionGap}>
      <View style={styles.kpiRow}>
        {summaryLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <View key={`kpi-loader-${index}`} style={[styles.kpiCard, { backgroundColor: '#EEF4FB', borderColor: '#D8E7F8' }]}>
              <View style={{ height: 12, width: '40%', backgroundColor: '#D9E6F7', borderRadius: 6, marginBottom: 14 }} />
              <View style={{ height: 28, width: '60%', backgroundColor: '#D9E6F7', borderRadius: 6, marginBottom: 12 }} />
              <View style={{ height: 12, width: '80%', backgroundColor: '#D9E6F7', borderRadius: 6 }} />
            </View>
          ))
        ) : (
          kpis.map((metric) => {
          const hideSubLabel = metric.id === 'sku-on' || metric.id === 'sku-off';

          return (
            <Pressable
              key={metric.id}
              style={[
                styles.kpiCard,
                Platform.OS === 'web' && hoveredKpiId === metric.id && styles.kpiCardHover,
              ]}
              onHoverIn={Platform.OS === 'web' ? () => setHoveredKpiId(metric.id) : undefined}
              onHoverOut={Platform.OS === 'web' ? () => setHoveredKpiId(null) : undefined}
            >
              <View style={styles.kpiLabelRow}>
                <Text style={styles.kpiLabel}>{metric.label}</Text>
                <DLOTooltip content={kpiTooltips[metric.id] || metric.subLabel} position="bottom" trigger="hover" maxWidth={260}>
                  <View style={styles.maxPotentialInfoBtn}>
                    <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                  </View>
                </DLOTooltip>
              </View>
              <Text
                style={[
                  styles.kpiValue,
                  metric.tone === 'success' && styles.kpiValueSuccess,
                  metric.tone === 'warning' && styles.kpiValueWarning,
                  metric.tone === 'danger' && styles.kpiValueDanger,
                ]}
              >
                {metric.value}
              </Text>
              {hideSubLabel ? null : <Text style={styles.kpiSubLabel}>{metric.subLabel}</Text> }
            </Pressable>
          );
        })
        )}
      </View>

      <View style={styles.execTrendGrid}>
        <View style={[styles.card, styles.execTrendCard]}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>SKU Recommendation Summary</Text>
            <Text style={styles.cardSubTitle}>Turn on and turn off candidates by confidence · drills by region, market, and location filters</Text>
          </View>
          {renderSkuRecommendationSummary(skuRecommendationSummary)}
        </View>

        <View style={[styles.card, styles.execTrendCard]}>
          <View style={styles.maxPotentialExecHeader}>
            <View style={styles.maxPotentialExecTitleWrap}>
              <View style={styles.kpiLabelRow}>
                <Text style={styles.cardTitle}>Max Potential Execution</Text>
                <DLOTooltip
                  content="Layer opportunity gap is the difference between current actual layer performance and the potential achievable if qualifying rules were fully adopted and used correctly."
                  position="bottom"
                  trigger="hover"
                  maxWidth={260}
                >
                  <View style={styles.maxPotentialInfoBtn}>
                    <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                  </View>
                </DLOTooltip>
              </View>
              <Text style={styles.cardSubTitle}>Actual layer volume vs layer opportunity</Text>
            </View>

            <View style={styles.maxPotentialToggleStack}>
              <View style={styles.maxPotentialToggleGroup}>
              <TouchableOpacity style={[styles.maxPotentialToggleBtn, maxPotentialViewMode === 'percent' && styles.maxPotentialToggleBtnActive]} onPress={() => setMaxPotentialViewMode('percent')}>
                <Text style={[styles.maxPotentialToggleBtnText, maxPotentialViewMode === 'percent' && styles.maxPotentialToggleBtnTextActive]}>%</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.maxPotentialToggleBtn, maxPotentialViewMode === 'cases' && styles.maxPotentialToggleBtnActive]} onPress={() => setMaxPotentialViewMode('cases')}>
                <Text style={[styles.maxPotentialToggleBtnText, maxPotentialViewMode === 'cases' && styles.maxPotentialToggleBtnTextActive]}>Cases</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.maxPotentialToggleHint}>
              {(() => {
                const level = (maxPotentialDrillLevel || '').toUpperCase();
                if (level === 'LOCATION') return 'Selected Locations';
                if (level === 'MARKET') return 'by MARKET / POD';
                return 'by Region / Unit';
              })()}
            </Text>
          </View>
        </View>
        {renderMaxPotentialExecution(maxPotentialExecution)}
        </View>
      </View>

      <View style={styles.execTrendGrid}>
        <View style={[styles.card, styles.execTrendCard]}>
          <View style={styles.cardHeader}>
            <View style={styles.kpiLabelRow}>
              <Text style={styles.cardTitle}>Enrollment Trend</Text>
              <DLOTooltip
                content="Time based view of enrollment performance showing whether adoption is improving and tracking against the leadership target of 75 percent."
                position="bottom"
                trigger="hover"
                maxWidth={260}
              >
                <View style={styles.maxPotentialInfoBtn}>
                  <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                </View>
              </DLOTooltip>
            </View>
            <Text style={styles.cardSubTitle}>{`Customer enrollment % · by ${formatTimeGrainLabel(enrollmentTimeGrain)}`}</Text>
          </View>
          {renderEnrollmentTrend(enrollmentTrend)}
        </View>

        <View style={[styles.card, styles.execTrendCard]}>
          <View style={styles.cardHeader}>
            <View style={styles.kpiLabelRow}>
              <Text style={styles.cardTitle}>Layer Performance Over Time</Text>
              <DLOTooltip
                content="Percentage of cases picked in full layers over time, one trend line per year. The current year is shown year to date."
                position="bottom"
                trigger="hover"
                maxWidth={260}
              >
                <View style={styles.maxPotentialInfoBtn}>
                  <MaterialIcons name="info-outline" size={14} color="#6B7280" />
                </View>
              </DLOTooltip>
            </View>
            <Text style={styles.cardSubTitle}>{`Layer performance % · by ${formatTimeGrainLabel(layerPerformanceTimeGrain)} filter`}</Text>
          </View>
          {renderLayerPerformance(layerPerformanceTrend)}
        </View>
      </View>
    </View>
  );
};

export default ExecutiveOverviewTab;
